import { Link, Outlet, useLocation, Navigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Logo } from "@/components/ui/logo";
import { Loader2, LayoutDashboard, FileText, BookOpen, Bug } from "lucide-react";
import { cn } from "@/lib/utils";

const adminNav = [
  { label: "Overview", to: "/admin", icon: LayoutDashboard, exact: true },
  { label: "Docs", to: "/admin/docs", icon: BookOpen },
  { label: "Blog", to: "/admin/blog", icon: FileText },
  { label: "Debug", to: "/admin/debug", icon: Bug },
];

export default function AdminLayout() {
  const { isAdmin, loading } = useAdminAuth();
  const { pathname } = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background relative">
      <div className="fixed inset-0 gradient-mesh pointer-events-none" />

      <header className="border-b border-border/50 bg-card/60 backdrop-blur-2xl sticky top-0 z-50">
        <div className="container flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-3">
            <Logo variant="square" className="h-8 w-8" />
            <div>
              <h1 className="text-lg font-semibold font-display text-gradient">Admin</h1>
              <p className="text-xs text-muted-foreground">Platform Management</p>
            </div>
          </div>
          <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back to App
          </Link>
        </div>

        {/* Tab navigation */}
        <div className="container px-6">
          <nav className="flex gap-1 -mb-px">
            {adminNav.map((item) => {
              const active = item.exact
                ? pathname === item.to
                : pathname.startsWith(item.to);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                    active
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </header>

      <Outlet />
    </div>
  );
}
